import { HttpClient } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';

import { SystemMessage } from 'app/core/system-message/system-message-service';
import { NotificationService } from 'app/notification.service';
import { AuditHistoryService } from 'app/shared/audit-history/auditHistory.service';
import { FormElementsModule } from 'app/shared/form-elements/form-elements.module';
import { SelectItem } from 'app/shared/models/select-item';
import { SvgHistoryStandaloneComponent } from 'app/shared/form-elements/svg/svg-history-standalone/svg-history-standalone.component';
import { UserWidgetVm } from 'app/shared/generated/Administration/Models/Users/UserWidgetVm';
import { WidgetPermissionTypeEnum } from 'app/core/enums/generated/WidgetPermissionTypeEnum';
import { WidgetTypeEnum } from 'app/core/enums/generated/WidgetTypeEnum';

@Component({
    selector: 'pcg-edit-widget',
    imports: [
        ReactiveFormsModule,
        FormElementsModule,
        SvgHistoryStandaloneComponent,
        NgbTooltip, 
        SelectButtonModule 
    ],
    templateUrl: './edit-widget.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './edit-widget.component.scss',
})
export class EditWidgetComponent implements OnInit, OnDestroy {

    route = inject(ActivatedRoute);
    http = inject(HttpClient);
    ns = inject(NotificationService);
    router = inject(Router);
    auditHistoryService = inject(AuditHistoryService)

    formGroup = UserWidgetVm.Form
    widgetId: number;
    userId: number;
    subscription = new Subscription();
    widgetTypeEnum = WidgetTypeEnum;

    widgetPermissionTypeEnum = WidgetPermissionTypeEnum;
    widgetPermissionTypeOptions: any[] = [
        { name: "Minimum", value: WidgetPermissionTypeEnum.ByMinimum }
        , { name: "Maximum", value: WidgetPermissionTypeEnum.ByMaximum }
        , { name: "Custom", value: WidgetPermissionTypeEnum.Custom }
    ];

    /**
     * The roles the chosen business area actually has, for all three permission pickers. Fetched per module rather
     * than left to the pickers' own dataSource: they fetch once when created - often before the widget has loaded -
     * and would then offer the generic role list, which has no BPHP User. Saving an Issuance widget from that list
     * would drop -1 and, through RemoveUserWidgets, pull the widget off every BPHP User's dashboard.
     */
    permissionRoles = signal<SelectItem[]>([]);

    ngOnInit() {
        this.widgetId = Number(this.route.snapshot.params.widgetId);
        this.userId = this.route.snapshot.params.id;
        this.setupModuleListener();
        this.getWidget();
        this.setupPermissionTypeListener();
    }

    setupModuleListener() {
        this.subscription.add(
            this.formGroup.controls.module.valueChanges
                .pipe(
                    startWith(this.formGroup.controls.module.value)
                    , distinctUntilChanged()
                    , switchMap(module => this.http.get<SelectItem[]>(
                        module == null
                            ? 'api/Select/PermissionRoles'
                            : `api/Select/PermissionRoles?module=${module}`
                    ))
                )
                .subscribe(items => this.permissionRoles.set(items ?? []))
        );
    }

    ngOnDestroy() { this.subscription.unsubscribe(); }

    setupPermissionTypeListener() {
        this.subscription.add(
            this.formGroup.controls.widgetPermissionType.valueChanges
                .subscribe(value => { this.updatePermissionValidators(value); })
        );
    }

    updatePermissionValidators(permissionType: WidgetPermissionTypeEnum) {
        const minControl = this.formGroup.controls.minPermission;
        const maxControl = this.formGroup.controls.maxPermission;
        const customControl = this.formGroup.controls.widgetPermissionRoleIds;

        minControl.clearValidators();
        maxControl.clearValidators();
        customControl.clearValidators();

        if (permissionType === WidgetPermissionTypeEnum.ByMinimum) {
            minControl.setValidators([Validators.required]);
        } else if (permissionType === WidgetPermissionTypeEnum.ByMaximum) {
            maxControl.setValidators([Validators.required]);
        } else if (permissionType === WidgetPermissionTypeEnum.Custom) {
            customControl.setValidators([Validators.required]);
        }

        minControl.updateValueAndValidity();
        maxControl.updateValueAndValidity();
        customControl.updateValueAndValidity();
    }

    getWidget() {
        this.http.get(`api/Admin/Widgets/GetWidget?widgetId=${this.widgetId}`).subscribe((msg: SystemMessage) => {
            if (msg.isSuccessful) { 
                this.formGroup.patchValue(msg.model);
                this.updatePermissionValidators(msg.model.widgetPermissionType);
            } 
            else { this.ns.showError(msg.message, { timeOut: 5000 }); }
        });
    }

    backButton() { this.router.navigateByUrl(`admin/system/widgets`); }

    navigateToAuditHistory() {
        this.auditHistoryService.openAuditHistory(
            this.widgetId
            , 'Administration.Models.Users.UserWidgetVm'
            , 'Widgets'
        )
    }

    onMessageSuccess(event: SystemMessage) { this.ns.showSuccess(event.message); }
    onMessageError(event: SystemMessage) { this.ns.showError(event.message); }
}